import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as XLSX from 'xlsx';
import { ArrowLeft, Search, X, Plus, Loader2, Pencil, Download } from 'lucide-react';
import { useInstitution } from '../../contexts/InstitutionContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissao } from '../../hooks/usePermissao';
import { Card, Input, Button } from '../../Components/ui';
import { useToast } from '../../Components/Feedback';
import { useApi } from '../../api/useApi';

// Detalhe de uma lista: título (editável clicando nele) + busca de aluno pra
// adicionar + membros atuais (cada um removível) — a "nota aberta" do estilo
// Apple Notas (ver Listas.js pra tela inicial com os títulos).
function ListaDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { institutionId } = useInstitution();
  const { token } = useAuth();
  const { podeEditar, podeExportar } = usePermissao('/listas');
  const queryClient = useQueryClient();
  const toast = useToast();

  const api = useApi();

  const { data: lista, isLoading } = useQuery({
    queryKey: ['lista', id],
    queryFn: async () => {
      const res = await api.get(`/api/listas/${id}`);
      return res?.data ?? res;
    },
    enabled: !!institutionId && !!token && !!id,
    refetchOnMount: 'always',
  });

  const recarregar = () => queryClient.invalidateQueries({ queryKey: ['lista', id] });
  const recarregarHome = () =>
    queryClient.invalidateQueries({ queryKey: ['listas', institutionId] });

  // --- Título editável inline ---
  const [editandoTitulo, setEditandoTitulo] = useState(false);
  const [tituloForm, setTituloForm] = useState('');

  useEffect(() => {
    if (lista) setTituloForm(lista.titulo);
  }, [lista]);

  const salvarTitulo = async () => {
    const titulo = tituloForm.trim();
    setEditandoTitulo(false);
    if (!titulo || titulo === lista.titulo) {
      setTituloForm(lista.titulo);
      return;
    }
    try {
      const res = await api.patch(`/api/listas/${id}`, { titulo });
      toast.success(res?.message || 'Título atualizado com sucesso.');
      recarregar();
      recarregarHome();
    } catch (error) {
      toast.error(error.message);
      setTituloForm(lista.titulo);
    }
  };

  // --- Busca de aluno + adicionar (mesmo padrão de Turmas.js "Matricular") ---
  const [buscaAluno, setBuscaAluno] = useState('');
  const [buscaAlunoDebounced, setBuscaAlunoDebounced] = useState('');
  const [processando, setProcessando] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setBuscaAlunoDebounced(buscaAluno), 300);
    return () => clearTimeout(timer);
  }, [buscaAluno]);

  const { data: resultadosBusca = [] } = useQuery({
    queryKey: ['busca-aluno-lista', institutionId, buscaAlunoDebounced],
    queryFn: async () => {
      const res = await api.get(`/api/alunos?nome=${encodeURIComponent(buscaAlunoDebounced)}`);
      const listaAlunos = res?.data ?? (Array.isArray(res) ? res : []);
      const idsJaNaLista = new Set((lista?.alunos || []).map((a) => a.id));
      return listaAlunos.filter((a) => !idsJaNaLista.has(a.id));
    },
    enabled: !!buscaAlunoDebounced && !!lista,
  });

  const adicionarAluno = async (aluno) => {
    setProcessando(true);
    try {
      const res = await api.post(`/api/listas/${id}/alunos`, { aluno_id: aluno.id });
      toast.success(res?.message || 'Aluno adicionado à lista.');
      setBuscaAluno('');
      recarregar();
      recarregarHome();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessando(false);
    }
  };

  // Exporta a lista em Excel com exatamente 2 colunas: nome e numero. O
  // "numero" prioriza o telefone do responsável; se ele não tiver, cai pro
  // telefone do próprio aluno (pedido explícito do usuário) — os dois vêm
  // separados do backend (ver GET /api/listas/:id em backend/listas.js).
  const exportarExcel = () => {
    const linhas = lista.alunos.map((a) => ({
      nome: a.nome,
      numero: a.telefone_responsavel || a.telefone_aluno || '',
    }));
    const worksheet = XLSX.utils.json_to_sheet(linhas);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Lista');
    XLSX.writeFile(workbook, `${lista.titulo}.xlsx`);
  };

  const removerAluno = async (aluno) => {
    setProcessando(true);
    try {
      const res = await api.del(`/api/listas/${id}/alunos/${aluno.id}`);
      toast.success(res?.message || 'Aluno removido da lista.');
      recarregar();
      recarregarHome();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessando(false);
    }
  };

  if (isLoading || !lista) {
    return (
      <div className="min-h-screen w-full bg-[#F9FAFB] dark:bg-gray-950 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#F9FAFB] dark:bg-gray-950">
      <main className="p-4 max-w-2xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/listas')}
            className="w-9 h-9 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center text-gray-500 dark:text-gray-400 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          {editandoTitulo ? (
            <input
              autoFocus
              value={tituloForm}
              onChange={(e) => setTituloForm(e.target.value)}
              onBlur={salvarTitulo}
              onKeyDown={(e) => {
                if (e.key === 'Enter') salvarTitulo();
                if (e.key === 'Escape') {
                  setTituloForm(lista.titulo);
                  setEditandoTitulo(false);
                }
              }}
              className="text-xl font-semibold text-gray-900 dark:text-gray-100 bg-transparent border-b-2 border-blue-500 focus:outline-none flex-1"
            />
          ) : podeEditar ? (
            <button
              type="button"
              onClick={() => setEditandoTitulo(true)}
              className="flex items-center gap-2 group flex-1 min-w-0 text-left"
            >
              <h1
                className="text-xl font-semibold text-gray-900 dark:text-gray-100 truncate"
                translate="no"
              >
                {lista.titulo}
              </h1>
              <Pencil className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 group-hover:text-gray-500 dark:group-hover:text-gray-400 flex-shrink-0" />
            </button>
          ) : (
            <h1
              className="text-xl font-semibold text-gray-900 dark:text-gray-100 truncate flex-1 min-w-0"
              translate="no"
            >
              {lista.titulo}
            </h1>
          )}
          {podeExportar && lista.alunos.length > 0 && (
            <Button variant="secondary" size="sm" icon={Download} onClick={exportarExcel}>
              Exportar Excel
            </Button>
          )}
        </div>

        <Card>
          {podeEditar && (
            <div className="relative">
              <Input
                placeholder="Buscar inscrito pelo nome para adicionar..."
                icon={Search}
                value={buscaAluno}
                onChange={(e) => setBuscaAluno(e.target.value)}
                disabled={processando}
              />
              {buscaAlunoDebounced && resultadosBusca.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg max-h-48 overflow-auto">
                  {resultadosBusca.map((aluno) => (
                    <button
                      key={aluno.id}
                      type="button"
                      onClick={() => adicionarAluno(aluno)}
                      disabled={processando}
                      className="w-full flex items-center justify-between px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors border-b border-gray-100 dark:border-gray-800 last:border-0 disabled:opacity-50"
                    >
                      <span translate="no">{aluno.nome}</span>
                      <Plus className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="mt-4">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
              {lista.alunos.length} aluno{lista.alunos.length === 1 ? '' : 's'} nessa lista
            </p>
            <div className="space-y-1.5">
              {lista.alunos.length === 0 ? (
                <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-6">
                  Nenhum aluno adicionado ainda.
                </p>
              ) : (
                lista.alunos.map((aluno) => (
                  <div
                    key={aluno.id}
                    className="flex items-center justify-between px-3 py-2 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/40"
                  >
                    <div className="min-w-0">
                      <span className="text-sm text-gray-900 dark:text-gray-100" translate="no">
                        {aluno.nome}
                      </span>
                      {(aluno.turno || aluno.turma) && (
                        <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">
                          {[aluno.turno, aluno.turma].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </div>
                    {podeEditar && (
                      <button
                        onClick={() => removerAluno(aluno)}
                        disabled={processando}
                        className="w-7 h-7 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 transition-colors disabled:opacity-50"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </Card>
      </main>
    </div>
  );
}

export default ListaDetalhe;
